/**
 * ChatInput Component
 * Message input with send button and enter-to-send support
 */

import React, { useState, useRef, KeyboardEvent } from 'react';
import { FiSend, FiLoader } from 'react-icons/fi';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  isSending?: boolean;
  placeholder?: string;
  maxLength?: number;
  className?: string;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  disabled = false,
  isSending = false,
  placeholder = 'Type your message...',
  maxLength = 2000,
  className = ''
}) => {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = () => {
    const trimmed = message.trim();
    if (trimmed && !disabled && !isSending) {
      onSend(trimmed);
      setMessage('');

      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Send on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);

    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  const canSend = message.trim().length > 0 && !disabled && !isSending;

  return (
    <div className={`bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 ${className}`}>
      <div className="flex items-end gap-3 p-4">
        {/* Textarea */}
        <div className="flex-1">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || isSending}
            maxLength={maxLength}
            rows={1}
            className="w-full bg-slate-900/50 text-gray-300 placeholder-gray-500 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none max-h-32"
          />

          {/* Character counter */}
          <div className="flex justify-between text-xs text-gray-500 mt-1 px-1">
            <span>Shift+Enter for new line</span>
            <span>{message.length} / {maxLength}</span>
          </div>
        </div>

        {/* Send Button */}
        <button
          onClick={handleSend}
          disabled={!canSend}
          className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center transition-colors ${
            canSend
              ? 'bg-blue-500 hover:bg-blue-600 text-white'
              : 'bg-slate-700 text-gray-500 cursor-not-allowed'
          }`}
          title="Send message (Enter)"
        >
          {isSending ? (
            <FiLoader className="animate-spin" size={20} />
          ) : (
            <FiSend size={20} />
          )}
        </button>
      </div>
    </div>
  );
};

export default ChatInput;
